import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { API_ENDPOINTS } from '@/config/api'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import TurnstileWidget from '@/components/TurnstileWidget'
import { cn } from '@/lib/utils'

const STEPS = [
  { id: 'business', title: 'Your business', blurb: 'Start with the spot you run.' },
  { id: 'scale', title: 'Size & volume', blurb: 'Helps us size hardware and pricing.' },
  { id: 'checkout', title: 'How you sell', blurb: 'So we match the right payments setup.' },
  { id: 'contact', title: 'Who to contact', blurb: 'The person we should reach for this business.' },
  { id: 'details', title: 'Almost done', blurb: 'A few extras, then we are off.' },
]

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant', hint: 'Dine-in, counter, or takeout' },
  { value: 'bar', label: 'Bar / tavern', hint: 'Drinks and bar food on site' },
  { value: 'cafe', label: 'Cafe / coffee', hint: 'Coffee, bakery, quick bites' },
  { value: 'retail', label: 'Retail shop', hint: 'Goods sold at a register' },
  { value: 'fast-casual', label: 'Fast casual', hint: 'Quick service with a menu' },
  { value: 'other', label: 'Something else', hint: 'Tell us in the notes later' },
]

const LOCATION_OPTIONS = [
  { value: '1', label: '1 location' },
  { value: '2-5', label: '2-5' },
  { value: '6-10', label: '6-10' },
  { value: '11-20', label: '11-20' },
  { value: '20+', label: '20+' },
]

const REVENUE_OPTIONS = [
  { value: 'under-10k', label: 'Under $10k' },
  { value: '10k-25k', label: '$10k-$25k' },
  { value: '25k-50k', label: '$25k-$50k' },
  { value: '50k-100k', label: '$50k-$100k' },
  { value: '100k-250k', label: '$100k-$250k' },
  { value: '250k+', label: '$250k+' },
]

const EMPLOYEE_OPTIONS = [
  { value: '1-5', label: '1-5' },
  { value: '6-15', label: '6-15' },
  { value: '16-30', label: '16-30' },
  { value: '31-50', label: '31-50' },
  { value: '50+', label: '50+' },
]

const PAYMENT_COLLECTION_OPTIONS = [
  { value: 'at-sale', label: 'When they buy', hint: 'Card charged at the counter, table, or register' },
  { value: 'tab-then-close', label: 'Open a tab, then close it', hint: 'Common for bars and table service' },
  { value: 'deposit-later', label: 'Deposit now, rest later', hint: 'Catering, events, or special orders' },
  { value: 'invoice-later', label: 'Invoice after delivery', hint: 'Pay later or on account' },
]

const DELIVERY_WINDOW_OPTIONS = [
  { value: 'same-visit', label: 'Same visit', hint: 'Minutes. Guest leaves with food, drinks, or goods' },
  { value: 'same-day', label: 'Same day', hint: 'Ready later today' },
  { value: 'within-week', label: 'Within a week', hint: 'Special orders or prep ahead' },
  { value: 'over-60-days', label: 'More than 60 days', hint: 'Long lead times or deposits far out' },
]

const AVERAGE_TICKET_OPTIONS = [
  { value: 'under-15', label: 'Under $15' },
  { value: '15-40', label: '$15-$40' },
  { value: '40-80', label: '$40-$80' },
  { value: '80-150', label: '$80-$150' },
  { value: '150+', label: '$150+' },
]

const HOW_HEARD_OPTIONS = [
  { value: 'website', label: 'Tillio website' },
  { value: 'referral', label: 'Referral' },
  { value: 'search', label: 'Google / search' },
  { value: 'social', label: 'Social media' },
  { value: 'partner', label: 'Partner / reseller' },
  { value: 'event', label: 'Event or trade show' },
  { value: 'other', label: 'Other' },
]

const EMPTY_FORM = {
  businessName: '',
  contactName: '',
  email: '',
  phone: '',
  businessType: '',
  numberOfLocations: '',
  currentPOS: '',
  monthlyRevenue: '',
  numberOfEmployees: '',
  paymentCollection: '',
  deliveryWindow: '',
  shipsOrDeliversLater: '',
  averageTicket: '',
  businessWebsite: '',
  howHeard: '',
  additionalInfo: '',
}

function ChoiceGrid({ options, value, onChange, columns = 2 }) {
  return (
    <div
      className={cn(
        'grid gap-3',
        columns === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
      )}
    >
      {options.map((option) => {
        const selected = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-xl border px-4 py-4 text-left transition-all',
              selected
                ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/30'
                : 'border-border bg-white hover:border-primary/40 hover:bg-slate-50'
            )}
          >
            <div className="font-medium text-foreground">{option.label}</div>
            {option.hint ? (
              <div className="mt-1 text-sm text-muted-foreground">{option.hint}</div>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function ChipRow({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
              selected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-white text-foreground hover:border-primary/40'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function OnboardingPage() {
  const navigate = useNavigate()
  const turnstileRef = useRef(null)
  const [step, setStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [captchaError, setCaptchaError] = useState(null)
  const [stepError, setStepError] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)

  const current = STEPS[step]

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    setStepError(null)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setField(name, value)
  }

  const resetTurnstile = () => {
    setTurnstileToken('')
    turnstileRef.current?.reset()
  }

  const validateStep = () => {
    if (step === 0) {
      if (!formData.businessName.trim()) {
        return 'Enter your business name.'
      }
      if (!formData.businessType) {
        return 'Pick the option that best fits your business.'
      }
    }
    if (step === 1 && !formData.numberOfLocations) {
      return 'Select how many locations you have.'
    }
    if (step === 2) {
      if (!formData.paymentCollection || !formData.deliveryWindow) {
        return 'Tell us when you take payment and how quickly guests get what they bought.'
      }
      if (!formData.shipsOrDeliversLater) {
        return 'Let us know if you ship or deliver after the sale.'
      }
    }
    if (step === 3) {
      if (!formData.contactName.trim() || !formData.email.trim() || !formData.phone.trim()) {
        return 'Fill in the contact name, email, and phone.'
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        return 'Enter a valid email address.'
      }
    }
    return null
  }

  const goNext = () => {
    const error = validateStep()
    if (error) {
      setStepError(error)
      return
    }
    setStepError(null)
    setStep((s) => Math.min(s + 1, STEPS.length - 1))
  }

  const goPrev = () => {
    setStepError(null)
    setStep((s) => Math.max(s - 1, 0))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (step < STEPS.length - 1) {
      goNext()
      return
    }

    const error = validateStep()
    if (error) {
      setStepError(error)
      return
    }

    setSubmitStatus(null)
    setCaptchaError(null)

    if (!turnstileToken) {
      setCaptchaError('Please complete the “I’m not a robot” check before submitting.')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(API_ENDPOINTS.onboarding, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          turnstileToken,
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setTurnstileToken('')
        setFormData(EMPTY_FORM)
        setStep(0)
      } else {
        setSubmitStatus('error')
        resetTurnstile()
      }
    } catch (err) {
      console.error('Error submitting form:', err)
      setSubmitStatus('error')
      resetTurnstile()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <SiteLayout navVariant="onboarding" footerVariant="minimal">
      <Seo
        title="Get Started with Tillio POS"
        description="Tell us about your restaurant, bar, or retail shop and get started with Tillio POS."
        path="/onboarding"
      />

      <div className="min-h-[calc(100vh-8rem)] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-50 via-white to-slate-50">
        <div className="container mx-auto max-w-2xl px-6 py-10 md:py-14">
          {submitStatus === 'success' ? (
            <div className="rounded-2xl border border-border bg-white p-10 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <svg className="h-9 w-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight">You are on the list</h1>
              <p className="mt-3 text-muted-foreground">
                We received your info and will reach out shortly to set up Tillio for your location.
              </p>
              <Button className="mt-8" onClick={() => navigate('/')}>
                Back to home
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary">
                      Step {step + 1} of {STEPS.length}
                    </p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight md:text-4xl">
                      {current.title}
                    </h1>
                    <p className="mt-2 text-muted-foreground">{current.blurb}</p>
                  </div>
                </div>
                <div className="flex gap-1.5" aria-hidden>
                  {STEPS.map((s, i) => (
                    <div
                      key={s.id}
                      className={cn(
                        'h-1.5 flex-1 rounded-full transition-colors',
                        i <= step ? 'bg-primary' : 'bg-slate-200'
                      )}
                    />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-white p-6 shadow-sm md:p-8">
                {step === 0 && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="businessName">Business name</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder="Maple & Main Cafe"
                        autoFocus
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>What kind of business?</Label>
                      <p className="text-sm text-muted-foreground">
                        Pick what fits most of your sales. You can refine this later.
                      </p>
                      <ChoiceGrid
                        options={BUSINESS_TYPES}
                        value={formData.businessType}
                        onChange={(value) => setField('businessType', value)}
                      />
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-8">
                    <div className="space-y-3">
                      <Label>Locations</Label>
                      <ChipRow
                        options={LOCATION_OPTIONS}
                        value={formData.numberOfLocations}
                        onChange={(value) => setField('numberOfLocations', value)}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Typical monthly card volume</Label>
                      <p className="text-sm text-muted-foreground">Estimate is fine.</p>
                      <ChipRow
                        options={REVENUE_OPTIONS}
                        value={formData.monthlyRevenue}
                        onChange={(value) => setField('monthlyRevenue', value)}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Team size</Label>
                      <ChipRow
                        options={EMPLOYEE_OPTIONS}
                        value={formData.numberOfEmployees}
                        onChange={(value) => setField('numberOfEmployees', value)}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Average ticket</Label>
                      <ChipRow
                        options={AVERAGE_TICKET_OPTIONS}
                        value={formData.averageTicket}
                        onChange={(value) => setField('averageTicket', value)}
                      />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-8">
                    <div className="space-y-3">
                      <Label>When do customers usually pay?</Label>
                      <ChoiceGrid
                        options={PAYMENT_COLLECTION_OPTIONS}
                        value={formData.paymentCollection}
                        onChange={(value) => setField('paymentCollection', value)}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>How soon do they get what they paid for?</Label>
                      <ChoiceGrid
                        options={DELIVERY_WINDOW_OPTIONS}
                        value={formData.deliveryWindow}
                        onChange={(value) => setField('deliveryWindow', value)}
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Do you ship or fulfill after the customer leaves?</Label>
                      <ChipRow
                        options={[
                          { value: 'no', label: 'No, in-store only' },
                          { value: 'sometimes', label: 'Sometimes' },
                          { value: 'yes', label: 'Yes, regularly' },
                        ]}
                        value={formData.shipsOrDeliversLater}
                        onChange={(value) => setField('shipsOrDeliversLater', value)}
                      />
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="contactName">Contact name</Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        placeholder="Jordan Lee"
                        autoFocus
                      />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@business.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="currentPOS">Current POS (optional)</Label>
                      <Input
                        id="currentPOS"
                        name="currentPOS"
                        value={formData.currentPOS}
                        onChange={handleChange}
                        placeholder="Square, Toast, Clover, cash only…"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessWebsite">Website (optional)</Label>
                      <Input
                        id="businessWebsite"
                        name="businessWebsite"
                        value={formData.businessWebsite}
                        onChange={handleChange}
                        placeholder="https://"
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>How did you hear about Tillio?</Label>
                      <ChipRow
                        options={HOW_HEARD_OPTIONS}
                        value={formData.howHeard}
                        onChange={(value) => setField('howHeard', value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="additionalInfo">Anything else?</Label>
                      <Textarea
                        id="additionalInfo"
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        placeholder="Hardware needs, go-live date, multiple concepts…"
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2 pt-2">
                      <TurnstileWidget
                        ref={turnstileRef}
                        onSuccess={(token) => {
                          setTurnstileToken(token)
                          setCaptchaError(null)
                        }}
                        onExpire={() => {
                          setTurnstileToken('')
                          setCaptchaError('Verification expired. Please check the box again.')
                        }}
                        onError={() => {
                          setTurnstileToken('')
                          setCaptchaError('Verification failed to load. Please refresh and try again.')
                        }}
                      />
                      {captchaError && <p className="text-sm text-red-700">{captchaError}</p>}
                    </div>
                  </div>
                )}

                {stepError && (
                  <p className="mt-5 text-sm text-red-700" role="alert">
                    {stepError}
                  </p>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-800">
                      Something went wrong. Try again or email services@tilliopayment.com.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={step === 0 ? () => navigate('/') : goPrev}
                  className="min-w-[7rem]"
                >
                  {step === 0 ? 'Cancel' : 'Back'}
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button type="button" onClick={goNext} className="min-w-[7rem]">
                    Continue
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting || !turnstileToken}
                    className="min-w-[7rem]"
                  >
                    {isSubmitting ? 'Sending…' : 'Submit'}
                  </Button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </SiteLayout>
  )
}

export default OnboardingPage
